import restful from '@/utils/restful';
import * as names from '@/store/names';
import * as get from '@/store/store';

/**
 * E37 · Evaluaciones de la Asignatura del Perfil de Egreso.
 *
 * El gemelo de nivel Matter ya vivía en `matters.js` (MATTER_EVALUATIONS); el de EPM no tenía
 * módulo y se pedía con `$restful` desde `EPMatterProgramEvaluation.vue`, que se guarda la lista
 * en estado local. Eso alcanzaba mientras una sola vista lo consumía.
 *
 * Con E37 la Actividad del Diseño Instruccional ES esta evaluación, así que el DI necesita
 * leerla y escribirla desde otro lugar de la aplicación. Un módulo Vuex es lo que permite que
 * los componentes del DI sigan haciendo `dispatch` como hasta ahora, en vez de subir cada
 * escritura al orquestador por eventos.
 *
 * Ojo: la vista del Programa sigue con su arreglo local, así que dentro de una misma sesión el
 * store y esa vista pueden divergir. No se unifican acá a propósito — son rutas distintas y
 * cada una refetchea al entrar.
 */
const state = {
    egress_profile_matter_evaluations: [],
    egress_profile_matter_evaluations_url: '/teaching/egress-profile-matter-evaluation',
}

const getters = {
    [names.EGRESS_PROFILE_MATTER_EVALUATIONS]: state => {
        if (!get.store.state.user) return [];
        return state.egress_profile_matter_evaluations
            .filter((x) => x.school == get.store.state.user.school)
            .sort((a, b) => a.order - b.order || a.id - b.id);
    },
}

const mutations = {
    [names.MUTATE_EGRESS_PROFILE_MATTER_EVALUATIONS]: (state, evaluations) => {
        evaluations.forEach(element => {
            let index = state.egress_profile_matter_evaluations.findIndex(x => x.id == element.id);
            if (index != -1) state.egress_profile_matter_evaluations.splice(index, 1, element);
            else state.egress_profile_matter_evaluations.push(element);
        });
    },
    /**
     * E37 · Reconcilia en vez de mezclar, para un `egress_profile_matter` concreto.
     *
     * Es el mismo arreglo que necesitó el store del DI en su momento (§2.5-quater del documento
     * técnico): `MUTATE_...` sólo agrega y actualiza, nunca saca lo que el backend ya no
     * devuelve, así que una evaluación borrada desde el Programa seguía a la vista en el DI
     * hasta un F5 — y peor, seguía sumando sus horas al acumulado de las semanas.
     *
     * Se acota a la asignatura pedida porque el store puede tener las de otras.
     */
    [names.MUTATE_SYNC_EGRESS_PROFILE_MATTER_EVALUATIONS]: (state, payload) => {
        const incoming_ids = payload.evaluations.map((x) => x.id);
        state.egress_profile_matter_evaluations = state.egress_profile_matter_evaluations.filter(
            (x) => x.egress_profile_matter != payload.egress_profile_matter_id ||
                incoming_ids.includes(x.id)
        );
        payload.evaluations.forEach(element => {
            let index = state.egress_profile_matter_evaluations.findIndex(x => x.id == element.id);
            if (index != -1) state.egress_profile_matter_evaluations.splice(index, 1, element);
            else state.egress_profile_matter_evaluations.push(element);
        });
    },
    [names.MUTATE_EGRESS_PROFILE_MATTER_EVALUATION]: (state, evaluation) => {
        let index = state.egress_profile_matter_evaluations.findIndex(x => x.id == evaluation.id);
        if (index != -1) state.egress_profile_matter_evaluations.splice(index, 1, evaluation);
        else state.egress_profile_matter_evaluations.push(evaluation);
    },
    [names.MUTATE_DELETE_EGRESS_PROFILE_MATTER_EVALUATION]: (state, evaluation_id) => {
        let index = state.egress_profile_matter_evaluations.findIndex(x => x.id == evaluation_id);
        if (index != -1) state.egress_profile_matter_evaluations.splice(index, 1);
    },
}

const actions = {
    [names.FETCH_EGRESS_PROFILE_MATTER_EVALUATIONS]: ({
        commit,
        state,
    }, egress_profile_matter_id) => {
        return new Promise((resolve) => {
            // GetAll y no Get: el endpoint viene paginado y con Get la lista quedaría siendo el
            // envelope {count, next, results} en vez de un arreglo.
            restful.GetAll(`${state.egress_profile_matter_evaluations_url}/?egress_profile_matter=${egress_profile_matter_id}`)
                .then(response => {
                    commit(names.MUTATE_SYNC_EGRESS_PROFILE_MATTER_EVALUATIONS, {
                        egress_profile_matter_id: egress_profile_matter_id,
                        evaluations: response,
                    });
                    resolve(response);
                });
        });
    },
    [names.FETCH_EGRESS_PROFILE_MATTER_EVALUATION]: ({
        commit,
        state
    }, evaluation_id) => {
        return new Promise((resolve) => {
            const evaluation = state.egress_profile_matter_evaluations.find(x => x.id == evaluation_id);
            if (evaluation) {
                resolve(evaluation);
                return;
            }
            restful.Get(`${state.egress_profile_matter_evaluations_url}/${evaluation_id}/`)
                .then(response => {
                    commit(names.MUTATE_EGRESS_PROFILE_MATTER_EVALUATION, response);
                    resolve(response);
                });
        });
    },
    [names.POST_EGRESS_PROFILE_MATTER_EVALUATION]: ({
        commit,
        state
    }, evaluation) => {
        return new Promise((resolve, reject) => {
            restful.Post(`${state.egress_profile_matter_evaluations_url}/`, evaluation)
                .then(response => {
                    commit(names.MUTATE_EGRESS_PROFILE_MATTER_EVALUATION, response);
                    resolve(response);
                })
                .catch(error => reject(error));
        });
    },
    // PUT con el objeto completo. Lo usa el formulario del Programa, que emite la evaluación
    // entera en vez de los campos que cambiaron — y desde E38 ese mismo formulario es el que
    // abre el Diseño Instruccional.
    [names.UPDATE_EGRESS_PROFILE_MATTER_EVALUATION]: ({
        commit,
        state
    }, evaluation) => {
        return new Promise((resolve, reject) => {
            restful.Put(`${state.egress_profile_matter_evaluations_url}/${evaluation.id}/`, evaluation)
                .then(response => {
                    commit(names.MUTATE_EGRESS_PROFILE_MATTER_EVALUATION, response);
                    resolve(response);
                })
                .catch(error => reject(error));
        });
    },
    [names.PATCH_EGRESS_PROFILE_MATTER_EVALUATION]: ({
        commit,
        state
    }, payload) => {
        return new Promise((resolve, reject) => {
            restful.Patch(`${state.egress_profile_matter_evaluations_url}/${payload.egress_profile_matter_evaluation_id}/`, payload.item)
                .then(response => {
                    commit(names.MUTATE_EGRESS_PROFILE_MATTER_EVALUATION, response);
                    resolve(response);
                })
                .catch(error => reject(error));
        });
    },
    [names.DELETE_EGRESS_PROFILE_MATTER_EVALUATION]: ({
        commit,
        state
    }, evaluation_id) => {
        return new Promise((resolve, reject) => {
            restful.Delete(`${state.egress_profile_matter_evaluations_url}/${evaluation_id}/`)
                .then(response => {
                    commit(names.MUTATE_DELETE_EGRESS_PROFILE_MATTER_EVALUATION, evaluation_id);
                    resolve(response);
                })
                .catch(error => reject(error));
        });
    },
}

export default {
    state,
    getters,
    mutations,
    actions,
};
